// Shared layout for Patients in Queue tiles — matches doctor PatientInQueue tiles
import dayjs from "dayjs";

export const QUEUE_TILE_GRID_PROPS = { xs: 12, sm: 6, md: 4, lg: 3 };

export const QUEUE_CARD_TEST_LIST_HEIGHT = 88;

/** Paper shell — same size/feel as doctor patient-in-queue tiles */
export const queueCardPaperSx = {
  p: 1,
  borderRadius: 2,
  border: "none",
  outline: "none",
  bgcolor: "background.paper",
  height: "100%",
  transition: "all 0.3s ease",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  "&:hover": {
    boxShadow: 6,
    transform: "translateY(-4px)",
  },
};

export const QUEUE_TOKEN_CHIP_SX = {
  backgroundColor: "transparent",
  color: "text.primary",
  fontWeight: "bold",
  fontSize: "0.65rem",
  height: "20px",
  px: 0.5,
  border: "none",
  "& .MuiChip-label": { px: 0.5 },
};

export const QUEUE_STATUS_CHIP_SX = {
  ml: 0.5,
  backgroundColor: "transparent",
  color: "text.secondary",
  fontWeight: 500,
  fontSize: "0.55rem",
  height: "18px",
  px: 0.5,
  border: "none",
  "& .MuiChip-label": { px: 0.5 },
};

export const QUEUE_ACTION_BTN_SX = {
  textTransform: "none",
  fontSize: "0.62rem",
  py: 0.2,
  px: 0.7,
  minHeight: 22,
  lineHeight: 1.2,
  flex: 1,
  minWidth: 0,
};

export const QUEUE_FIELD_LABEL_SX = {
  fontSize: "0.55rem",
  lineHeight: 1,
};

export const QUEUE_FIELD_VALUE_SX = {
  fontSize: "0.7rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.2,
};

/** Matches QueueField icon width so Test/Status text lines up with Patient/Doctor/Date/Time */
export const QUEUE_FIELD_ICON_SLOT_SX = {
  width: "0.7rem",
  height: "0.7rem",
  flexShrink: 0,
};

export const QUEUE_FIELDS_ROW_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.5,
  justifyContent: "space-between",
};

/** @deprecated Fixed-width layout removed — Grid tiles are used instead */
export const QUEUE_CARD_WIDTH = "100%";
export const queueCardWrapperSx = {
  width: "100%",
  height: "100%",
};
export const queueCardsContainerSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  alignContent: "flex-start",
};

/**
 * Parse hospital datetimes for queue tiles.
 *
 * - No timezone (`2026-08-18T10:30:00` or Jackson `[2026,8,18,10,30]`):
 *   treat as hospital wall-clock (do not shift).
 * - Explicit UTC/offset (`...Z`, `...+00:00`, `...+05:30`):
 *   convert to the browser's local timezone so Azure UTC and local IST match.
 */
const DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_ONLY_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i;

const isMidnightClock = (h, min, s) => Number(h) === 0 && Number(min) === 0 && Number(s || 0) === 0;

/** True when the string carries an explicit zone (UTC Z or ±offset). */
const hasExplicitTimezone = (str) =>
  /[zZ]\s*$/.test(str) || /[+-]\d{2}:?\d{2}\s*$/.test(str);

export const displayQueueId = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    return displayQueueId(
      value.id ?? value.appointmentId ?? value.appointment_id ?? value.prescriptionId
    );
  }
  const text = String(value).trim();
  if (!text || text === "0" || text.toLowerCase() === "null") return "—";
  return text;
};

export const parseHospitalDateTime = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const parsed = dayjs(value);
    if (!parsed.isValid()) return null;
    return {
      parsed,
      hasTime: !isMidnightClock(value.getHours(), value.getMinutes(), value.getSeconds()),
    };
  }

  if (
    Array.isArray(value) &&
    value.length >= 3 &&
    typeof value[0] === "number" &&
    value[0] >= 1900
  ) {
    const [y, m, d, h = 0, min = 0, s = 0] = value;
    const parsed = dayjs(new Date(y, (m || 1) - 1, d || 1, h, min, s));
    if (!parsed.isValid()) return null;
    return { parsed, hasTime: value.length >= 4 && !isMidnightClock(h, min, s) };
  }

  if (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === "number" &&
    value[0] <= 23 &&
    (value[1] == null || value[1] <= 59)
  ) {
    const [h, min = 0, s = 0] = value;
    const parsed = dayjs().hour(h).minute(min).second(s).millisecond(0);
    return { parsed, hasTime: !isMidnightClock(h, min, s), timeOnly: true };
  }

  if (typeof value === "object") return null;

  const str = String(value).trim();
  if (!str) return null;

  const dt = str.match(DATETIME_RE);
  if (dt) {
    let parsed;
    if (hasExplicitTimezone(str)) {
      // Azure / Jackson Instant often serializes as UTC with Z — show local clock.
      parsed = dayjs(str);
    } else {
      // Local/org DB wall-clock without zone — do not shift by browser offset.
      const y = Number(dt[1]);
      const m = Number(dt[2]);
      const d = Number(dt[3]);
      const h = Number(dt[4]);
      const min = Number(dt[5]);
      const s = Number(dt[6] || 0);
      parsed = dayjs(new Date(y, m - 1, d, h, min, s));
    }
    if (!parsed.isValid()) return null;
    return {
      parsed,
      hasTime: !isMidnightClock(parsed.hour(), parsed.minute(), parsed.second()),
    };
  }

  const dateOnly = str.match(DATE_ONLY_RE);
  if (dateOnly) {
    const parsed = dayjs(
      new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    );
    return parsed.isValid() ? { parsed, hasTime: false } : null;
  }

  const timeOnly = str.match(TIME_ONLY_RE);
  if (timeOnly) {
    let h = Number(timeOnly[1]);
    const min = Number(timeOnly[2]);
    const s = Number(timeOnly[3] || 0);
    const ampm = (timeOnly[4] || "").toUpperCase();
    if (ampm === "PM" && h < 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    const parsed = dayjs().hour(h).minute(min).second(s).millisecond(0);
    return { parsed, hasTime: !isMidnightClock(h, min, s), timeOnly: true };
  }

  const parsed = dayjs(str);
  if (!parsed.isValid()) return null;
  return {
    parsed,
    hasTime:
      (/T\d{1,2}:\d{2}/.test(str) || hasExplicitTimezone(str)) &&
      !isMidnightClock(parsed.hour(), parsed.minute(), parsed.second()),
  };
};

const collectDateTimeCandidates = (...candidates) => {
  const values = [];
  const pushValue = (value) => {
    if (value == null || value === "") return;
    values.push(value);
  };

  const pushFromObject = (item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return;
    pushValue(item.appointmentDateTime);
    pushValue(item.appointment_date_time);
    pushValue(item.appointmentDate);
    pushValue(item.createdAt);
    pushValue(item.created_at);
    pushValue(item.reportDate);
    pushValue(item.report_date);
    pushValue(item.testDate);
    pushValue(item.test_date);
    pushValue(item.admissionDateTime);
    pushValue(item.admissionTime);
    pushValue(item.admission_time);
    pushValue(item.admissionDate);
  };

  candidates.forEach((value) => {
    if (value == null || value === "") return;

    if (
      Array.isArray(value) &&
      value.length >= 2 &&
      typeof value[0] === "number"
    ) {
      pushValue(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => pushFromObject(item));
      return;
    }

    if (typeof value === "object" && !(value instanceof Date)) {
      pushFromObject(value);
      return;
    }

    pushValue(value);
  });

  return values;
};

export const resolveQueueDateTimeParsed = (...candidates) => {
  const parsedList = collectDateTimeCandidates(...candidates)
    .map(parseHospitalDateTime)
    .filter(Boolean);
  return parsedList.find((item) => item.hasTime) || parsedList[0] || null;
};

export const formatQueueDateTimeLabels = (...candidates) => {
  const parsedList = collectDateTimeCandidates(...candidates)
    .map(parseHospitalDateTime)
    .filter(Boolean);
  const withTime = parsedList.find((item) => item.hasTime);
  const withDate = parsedList.find((item) => item.parsed && !item.timeOnly);
  const dateSource = withDate || withTime;
  return {
    dateLabel: dateSource?.parsed ? dateSource.parsed.format("DD MMM YYYY") : "—",
    timeLabel: withTime?.parsed ? withTime.parsed.format("h:mm A") : "—",
  };
};

/**
 * Prefer a value that includes a real clock time over date-only midnight.
 * Returns a dayjs instance or null (legacy helper).
 */
export const resolveQueueDateTime = (...candidates) => {
  const resolved = resolveQueueDateTimeParsed(...candidates);
  return resolved?.parsed || null;
};
